import { Box, Paper, Skeleton, Stack } from '@mui/material';

/**
 * Skeleton loaders for async content (brief §6: skeletons, never spinners for page or table
 * loads).
 *
 * A spinner says only "wait"; a skeleton also says what is arriving and roughly how much of
 * it, so the layout does not jump when data lands. That reflow is the actual complaint behind
 * most "the app feels slow" feedback.
 */

export function PageSkeleton() {
  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      <Skeleton variant="text" width={240} height={40} />
      <Skeleton variant="text" width={420} height={24} />
      <Skeleton variant="rounded" height={320} />
    </Stack>
  );
}

interface GridSkeletonProps {
  rows?: number;
  columns?: number;
}

/** Mirrors the DataGrid's toolbar, header row and body so the swap is not a visible jump. */
export function GridSkeleton({ rows = 10, columns = 6 }: GridSkeletonProps) {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 1, p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Skeleton variant="rounded" width={200} height={32} />
        <Box sx={{ flex: 1 }} />
        <Skeleton variant="rounded" width={88} height={32} />
        <Skeleton variant="rounded" width={88} height={32} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, px: 2, py: 1.5, bgcolor: 'action.hover' }}>
        {Array.from({ length: columns }, (_, index) => (
          <Skeleton key={index} variant="text" sx={{ flex: 1 }} height={20} />
        ))}
      </Box>

      {Array.from({ length: rows }, (_, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{ display: 'flex', gap: 2, px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}
        >
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton key={columnIndex} variant="text" sx={{ flex: 1 }} height={18} />
          ))}
        </Box>
      ))}
    </Paper>
  );
}

export function CardSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Skeleton variant="text" width="40%" height={28} />
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} variant="text" height={20} />
      ))}
    </Paper>
  );
}
